import { useSyncExternalStore } from 'react'
import { onlineManager } from '@tanstack/react-query'

/**
 * Seeds the connection state from the browser instead of assuming it, and keeps
 * it in step from then on.
 *
 * `onlineManager` starts at "online" and only ever learns otherwise from an
 * `offline` event. That is fine for a tab that was open when the signal went,
 * and wrong for the case this whole issue is about: an app opened cold in a
 * cellar has already missed the event, so it fetches, fails, and shows an error
 * over screens it had the saved answer to. Reading `navigator.onLine` as the
 * listeners go on is what closes that gap — the browser knew all along.
 *
 * Replacing the query layer's event listener rather than calling `setOnline`
 * from somewhere in the app, because this is the extension point it provides:
 * the setup runs where the listeners are attached, so there is one place that
 * decides what "online" means and everything reads it from there.
 */
export function watchConnection(): void {
  onlineManager.setEventListener((setOnline) => {
    const update = () => setOnline(navigator.onLine)

    update()
    window.addEventListener('online', update, false)
    window.addEventListener('offline', update, false)

    return () => {
      window.removeEventListener('online', update)
      window.removeEventListener('offline', update)
    }
  })
}

watchConnection()

/**
 * Whether the app believes it can reach the API (issue #64).
 *
 * Read from the `onlineManager` rather than from `navigator` directly, so the
 * indicator in the shell and the query layer can never disagree: the manager is
 * what decides whether a query fetches or waits, and a chip saying nothing over
 * a screen full of paused queries would be worse than no chip at all.
 *
 * `navigator.onLine` is optimistic — true means a network was joined, not that
 * anything is reachable across it, so a theatre wifi that associates without a
 * route still reads as online. That is the honest limit of what a browser will
 * tell you for free, and it is why a failed request answers with a sentence
 * (`OfflineError`) rather than trusting this. Reachability, as opposed to
 * connectivity, is the sync loop's problem and arrives with the queue.
 */
export function useIsOnline(): boolean {
  return useSyncExternalStore(subscribe, isOnline, assumeOnline)
}

/*
 * All three at module scope, and load-bearing there. React re-subscribes
 * whenever the `subscribe` it is given changes identity, and re-subscribing
 * here means the manager's last listener going and coming back, which re-runs
 * the setup above and re-seeds the state from `navigator.onLine`. Written
 * inline, that turns every change of connection into a change immediately
 * undone — the chip appears and vanishes in the same tick.
 */
const subscribe = (onStoreChange: () => void) => onlineManager.subscribe(() => onStoreChange())
const isOnline = () => onlineManager.isOnline()
// No server rendering here; assuming online is the value that renders nothing
// rather than a chip nobody asked for.
const assumeOnline = () => true
